import { getEffectiveShippingSelection } from "@/utils/shippingOption";
import { memo } from "react";
import {
  ShippingMethodSelect,
  type ShippingMethodSelectVariant,
  type ShippingOption
} from "./ShippingMethodSelect";

export interface ShippingSelectorProps {
  /**
   * Available shipping options for this cart, from `useShippingMethodSync`.
   */
  options: ShippingOption[];
  /**
   * Whether options are still loading, from `useShippingMethodSync`.
   */
  isLoading?: boolean;
  /**
   * Whether a shipping method change is in flight, from `useShippingMethodSync`.
   */
  isPending?: boolean;
  /**
   * Whether `selectedOptionId` is a valid choice among `options`, from
   * `useShippingMethodSync`. Drives the placeholder fallback below - it is
   * not itself a validation-error signal, see {@link error}.
   */
  isSelectionValid?: boolean;
  /**
   * The cart's currently selected option id (the composite `base::handle`
   * picker id - see `getSelectedShippingPickerId`, KAN-108), from
   * `useShippingMethodSync`.
   */
  selectedOptionId?: string | null;
  /**
   * Called with the newly chosen option id.
   */
  onSelect: (optionId: string) => void;
  /**
   * Visual variant, forwarded to {@link ShippingMethodSelect}.
   */
  variant?: ShippingMethodSelectVariant;
  /**
   * The cart's charged shipping amount (`cart.totals.shipping_total`),
   * forwarded to {@link ShippingMethodSelect} so the inline collapsed value
   * shows what the cart actually charges rather than the option list price.
   */
  displayAmount?: number | null;
  /**
   * Shows the field's validation-error state (e.g. checkout's "must select
   * before proceeding" red border). Distinct from `isSelectionValid`: the
   * cart page treats an invalid selection as "show the placeholder", never
   * as an error to flag.
   */
  error?: boolean;
  /**
   * Disables the control, forwarded to {@link ShippingMethodSelect}.
   */
  disabled?: boolean;
  /**
   * Aria label for accessibility, forwarded to {@link ShippingMethodSelect}.
   */
  ariaLabel?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Dropdown selector for shipping options per seller cart.
 *
 * Presentational: the caller owns its own `useShippingMethodSync(cartId,
 * selectedOptionId)` call and passes the result in - so a caller that also
 * needs that hook's other fields (e.g. `freeShippingThreshold`) calls it
 * once and shares the result, rather than this component calling the hook a
 * second time behind the caller's back (two independent mutation instances
 * would then disagree about whether a change is in flight).
 */
export const ShippingSelector = memo(function ShippingSelector({
  options,
  isLoading,
  isPending,
  isSelectionValid = true,
  selectedOptionId,
  onSelect,
  variant,
  displayAmount,
  error,
  disabled,
  ariaLabel = "Select shipping method",
  className
}: ShippingSelectorProps) {
  // Falls back to the placeholder when there's no usable selection - whether
  // because nothing is chosen yet or because the choice is invalid.
  const effectiveSelectedId = getEffectiveShippingSelection(
    isSelectionValid,
    selectedOptionId
  );

  return (
    <ShippingMethodSelect
      options={options}
      variant={variant}
      selectedOptionId={effectiveSelectedId}
      // While the change is in flight the cart's `shipping_total` still
      // reflects the PREVIOUS method, so showing it beside the newly-chosen
      // name would read as "Express · Free". Dropping it falls back to the
      // chosen option's own list price, which is the figure the buyer just
      // picked from; the authoritative amount takes over when the cart
      // refetch lands.
      displayAmount={isPending ? undefined : displayAmount}
      onChange={onSelect}
      isLoading={isLoading}
      isPending={isPending}
      error={error}
      disabled={disabled}
      ariaLabel={ariaLabel}
      className={className}
    />
  );
});
